import {
  Button,
  Descriptions,
  Radio,
  Modal,
  Tag,
  Flex,
  Tooltip,
  Input,
  theme,
  message,
  Select,
  Spin,
  Segmented,
} from 'antd';
import { getMemberByIdV2, getMemberListV2, editUmsMemberTags } from 'apis/ums';
import { getMemberOrderListSmall } from 'apis/oms';
import { UmsMember } from 'types/ums';
import { UnwrapPromise } from 'types/base';
import { useToggle } from 'react-use';
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { usePayloadContext } from 'pages/oms/checkout-counter/utils/payload-context';
import { ActionType } from 'pages/oms/checkout-counter/utils/payload-reducer';
import LOCALS from '../commons/locals';
import i18n from 'i18n';
import CreateMemberDrawer from './create-member-drawer';
import BarcodeScanner from './barcode-scanner';
import { Link } from 'react-router-dom';
import formatTime from 'utils/formatTime';
import { debounce, throttle } from 'lodash-es';
import { Trans } from 'react-i18next';
import { PlusOutlined, ScanOutlined } from '@ant-design/icons';
import useSysDict from 'commons/hooks/use-sys-dict';
import { parseMemberQrValue } from 'utils/member-qr';
import {
  storeScannerBeepAudioError,
  storeScannerBeepAudioSuccess,
} from 'commons';
import type { InputRef } from 'antd';

const tagInputStyle: React.CSSProperties = {
  width: 64,
  height: 22,
  marginInlineEnd: 8,
  verticalAlign: 'top',
};

// 会员类型常量
const MEMBER_TYPE = {
  NEW_MEMBER: 'NEW_MEMBER',
  RETURNING_CUSTOMER: 'RETURNING_CUSTOMER',
  NON_PURCHASER: 'NON_PURCHASER',
} as const;

type MemberTypeKey = (typeof MEMBER_TYPE)[keyof typeof MEMBER_TYPE];

// 会员类型国际化映射
const getMemberTypeText = (type: MemberTypeKey | null): string => {
  if (!type) return '';

  const typeMap: Record<MemberTypeKey, string> = {
    [MEMBER_TYPE.NEW_MEMBER]: i18n.t('LgfgKzLakm') || '新会员',
    [MEMBER_TYPE.RETURNING_CUSTOMER]: i18n.t('aZlHFzBReU') || '回头客',
    [MEMBER_TYPE.NON_PURCHASER]: i18n.t('rutZCCoEKZ') || '未购买',
  };

  return typeMap[type];
};

const UmsMemberTable = ({
  umsMember,
  memberType,
}: {
  umsMember?: UmsMember;
  memberType?: MemberTypeKey | null;
}) => {
  const { dispatch, state } = usePayloadContext();
  const [tags, setTags] = useState<string[]>(umsMember?.tags || []);
  const [inputValue, setInputValue] = useState('');
  const [editInputIndex, setEditInputIndex] = useState(-1);
  const [editInputValue, setEditInputValue] = useState('');
  const editInputRef = useRef<InputRef>(null);
  const [isModalOpen, toggleIsModalOpen] = useToggle(false);
  const tagConfig = useSysDict<[string[], { [key: string]: string }]>(
    'ums_member_tags_config',
  );

  useEffect(() => {
    editInputRef.current?.focus();
  }, [editInputValue]);

  useEffect(() => {
    setTags(umsMember?.tags || []);
  }, [umsMember?.tags]);

  const handleClose = (removedTag: string) => {
    if (!umsMember?.id) return;
    const newTags = tags.filter((tag) => tag !== removedTag);
    editUmsMemberTags({ id: umsMember.id, tags: newTags });
    setTags(newTags);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputConfirm = async (inputValue: string) => {
    if (!umsMember?.id) return;
    if (inputValue && !tags.includes(inputValue)) {
      const newTags = [...tags, inputValue];
      setTags(newTags);
      editUmsMemberTags({ id: umsMember.id, tags: newTags });
    }
    setInputValue('');
    toggleIsModalOpen(false);
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditInputValue(e.target.value);
  };

  const handleEditInputConfirm = () => {
    if (!umsMember?.id) return;
    const newTags = [...tags];
    newTags[editInputIndex] = editInputValue;
    setTags(newTags);
    editUmsMemberTags({ id: umsMember.id, tags: newTags });
    setEditInputIndex(-1);
    setEditInputValue('');
  };

  const { token } = theme.useToken();

  const tagPlusStyle: React.CSSProperties = {
    height: 22,
    background: token.colorBgContainer,
    borderStyle: 'dashed',
  };

  return (
    <div>
      {umsMember && (
        <Descriptions bordered size="small">
          {/* <Descriptions.Item label="ID">{umsMember.id}</Descriptions.Item> */}
          <Descriptions.Item label={i18n.t(LOCALS.email) || '邮箱'}>
            <Link to={`/ums/member-view/${umsMember.id}`} target="_blank">
              {umsMember.email}
            </Link>
          </Descriptions.Item>
          <Descriptions.Item label={i18n.t(LOCALS.name) || '用户名'}>
            <div className="flex items-center gap-2">
              <span>{umsMember.showName}</span>
              {memberType && (
                <Tag
                  color={
                    memberType === MEMBER_TYPE.NEW_MEMBER
                      ? 'green'
                      : memberType === MEMBER_TYPE.RETURNING_CUSTOMER
                      ? 'blue'
                      : 'default'
                  }
                >
                  {getMemberTypeText(memberType)}
                </Tag>
              )}
            </div>
          </Descriptions.Item>
          <Descriptions.Item
            label={i18n.t(LOCALS.available_points) || '可用积分'}
          >
            <div className="text-red-500 font-bold">
              {umsMember.integration}
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={i18n.t(LOCALS.phone_number) || '电话'}>
            {umsMember.showPhone}
          </Descriptions.Item>
          <Descriptions.Item
            label={i18n.t(LOCALS.registration_time) || '注册时间'}
          >
            {formatTime(umsMember.createTime)}
          </Descriptions.Item>
          <Descriptions.Item label={<Trans i18nKey={LOCALS.xSEiAdbHmA} />}>
            ¥{umsMember.last365DaysAmount || 0}
          </Descriptions.Item>
          <Descriptions.Item label={<Trans i18nKey={LOCALS.member_level} />}>
            {umsMember.memberLevel || '-'}
          </Descriptions.Item>

          <Descriptions.Item label={i18n.t(LOCALS.points_earned || '赠送积分')}>
            <Radio.Group
              buttonStyle="solid"
              value={state.isPointsGiven}
              onChange={(e) =>
                dispatch({
                  type: ActionType.UPDATE_BATCH,
                  payload: { isPointsGiven: e.target.value },
                })
              }
            >
              <Radio.Button value={true}>
                <Trans i18nKey={LOCALS.yes} />
              </Radio.Button>
              <Radio.Button value={false}>
                <Trans i18nKey={LOCALS.no} />
              </Radio.Button>
            </Radio.Group>
          </Descriptions.Item>

          <Descriptions.Item
            label={<Trans i18nKey={LOCALS.khvsAbbLJs} />}
            span={2}
          >
            <Modal
              title="New Tag"
              open={isModalOpen}
              footer={null}
              onCancel={toggleIsModalOpen}
            >
              <div className="grid gap-4">
                <div className="flex gap-1">
                  {tagConfig &&
                    tagConfig[0].map((tag) => {
                      return (
                        <Tag
                          color={tagConfig[1][tag] || ''}
                          className="cursor-pointer"
                          key={tag}
                          onClick={() => {
                            handleInputConfirm(tag);
                          }}
                        >
                          {tag}
                        </Tag>
                      );
                    })}
                </div>
                <Input
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  onBlur={() => {
                    handleInputConfirm(inputValue);
                  }}
                  onPressEnter={() => {
                    handleInputConfirm(inputValue);
                  }}
                ></Input>
              </div>
            </Modal>

            <Flex gap="4px 0" wrap="wrap">
              {tags.map<React.ReactNode>((tag, index) => {
                if (editInputIndex === index) {
                  return (
                    <Input
                      ref={editInputRef}
                      key={tag}
                      size="small"
                      style={tagInputStyle}
                      value={editInputValue}
                      onChange={handleEditInputChange}
                      onBlur={handleEditInputConfirm}
                      onPressEnter={handleEditInputConfirm}
                    />
                  );
                }

                const isLongTag = tag.length > 20;
                const tagElem = (
                  <Tag
                    key={tag}
                    closable={true}
                    style={{ userSelect: 'none' }}
                    onClose={() => handleClose(tag)}
                    color={(tagConfig && tagConfig[1][tag]) || ''}
                  >
                    <span
                      onDoubleClick={(e) => {
                        setEditInputIndex(index);
                        setEditInputValue(tag);
                        e.preventDefault();
                      }}
                    >
                      {isLongTag ? `${tag.slice(0, 20)}...` : tag}
                    </span>
                  </Tag>
                );
                return isLongTag ? (
                  <Tooltip title={tag} key={tag}>
                    {tagElem}
                  </Tooltip>
                ) : (
                  tagElem
                );
              })}

              <Tag
                className="cursor-pointer"
                style={tagPlusStyle}
                icon={<PlusOutlined />}
                onClick={toggleIsModalOpen}
              >
                New Tag
              </Tag>
            </Flex>
          </Descriptions.Item>
        </Descriptions>
      )}
    </div>
  );
};

const toUmsMember = (
  member: Awaited<ReturnType<typeof getMemberByIdV2>>,
): UmsMember => {
  const { firstName, lastName, nickname, phone, countryCode } = member;
  let showName = '';
  if (firstName && lastName) {
    showName = `${firstName} ${lastName}`;
  } else {
    showName = nickname || member.email || '-';
  }

  return {
    ...member,
    showName,
    showPhone: countryCode && phone ? `+${countryCode} ${phone}` : '-',
    firstNameKatakana: member.firstNameKatakana ?? '',
    lastNameKatakana: member.lastNameKatakana ?? '',
    countryCode: member.countryCode ?? '',
    detailAddress: member.detailAddress ?? '',
    lastLoginTime: member.lastLoginTime ?? null,
  };
};

const MEMBER_SELECT_MODE = {
  SCAN: 'scan',
  MANUAL: 'manual',
} as const;

type MemberSelectMode =
  (typeof MEMBER_SELECT_MODE)[keyof typeof MEMBER_SELECT_MODE];

const UmsMemberSelect = () => {
  const [umsMember, setUmsMember] = useState<UmsMember | undefined>();
  const [umsMemberList, setUmsMemberList] = useState<
    UnwrapPromise<ReturnType<typeof getMemberListV2>>['list']
  >([]);
  const [memberType, setMemberType] = useState<MemberTypeKey | null>(null);
  const [selectMode, setSelectMode] = useState<MemberSelectMode>(
    MEMBER_SELECT_MODE.SCAN,
  );
  const { dispatch } = usePayloadContext();
  const [createMemberOpen, toggleCreateMemberOpen] = useToggle(false);
  const [scannerOpen, toggleScannerOpen] = useToggle(false);
  const [loading, toggleLoading] = useToggle(false);

  const checkMemberType = useCallback(async (member: UmsMember) => {
    if (!member) {
      setMemberType(null);
      return;
    }

    // 检查是否为当天注册
    const createTime = new Date(member.createTime);
    const today = new Date();
    const isToday = createTime.toDateString() === today.toDateString();

    if (isToday) {
      setMemberType(MEMBER_TYPE.NEW_MEMBER);
      return;
    }

    try {
      // 调用API获取会员订单
      const { list } = await getMemberOrderListSmall({
        memberId: member.id,
        pageNum: 1,
        pageSize: 20, // 获取足够多的订单来判断
      });

      if (list.length === 0) {
        setMemberType(MEMBER_TYPE.NON_PURCHASER);
        return;
      }

      // 检查是否有状态为1,2,3的订单
      const hasValidOrder = list.some(
        (order) =>
          order.status === 1 || order.status === 2 || order.status === 3,
      );

      setMemberType(
        hasValidOrder
          ? MEMBER_TYPE.RETURNING_CUSTOMER
          : MEMBER_TYPE.NON_PURCHASER,
      );
    } catch (error) {
      console.error('检查会员类型失败:', error);
      setMemberType(MEMBER_TYPE.NON_PURCHASER);
    }
  }, []);

  const selectMember = useCallback(
    (member: UmsMember) => {
      setUmsMember(member);
      checkMemberType(member);
      dispatch({
        type: ActionType.UPDATE_BATCH,
        payload: {
          memberId: member.id,
          totalIntegration: member.integration,
          isPointsGiven: true,
        },
      });
    },
    [checkMemberType, dispatch],
  );

  const handleMemberScanSuccess = useMemo(() => {
    return throttle(
      async (text: string) => {
        const memberId = parseMemberQrValue(text);
        if (!memberId) {
          storeScannerBeepAudioError
            .play()
            .catch((err) =>
              console.log('Autoplay requires user interaction:', err),
            );
          message.error({
            content: i18n.t(LOCALS.member_qr_invalid) || '无效的会员二维码',
            key: 'memberQrInvalid',
          });
          return;
        }

        toggleLoading(true);
        try {
          const memberData = await getMemberByIdV2(memberId);
          const member = toUmsMember(memberData);
          storeScannerBeepAudioSuccess
            .play()
            .catch((err) =>
              console.log('Autoplay requires user interaction:', err),
            );
          selectMember(member);
          toggleScannerOpen(false);
        } catch {
          storeScannerBeepAudioError
            .play()
            .catch((err) =>
              console.log('Autoplay requires user interaction:', err),
            );
          message.error({
            content: i18n.t(LOCALS.member_qr_not_found) || '未找到该会员',
            key: 'memberNotFound',
          });
        } finally {
          toggleLoading(false);
        }
      },
      1000,
      { leading: true, trailing: false },
    );
  }, [selectMember, toggleLoading, toggleScannerOpen]);

  const debouncedHandleSearch = useMemo(() => {
    return debounce((keyword: string) => {
      keyword = keyword.trim();
      if (!keyword) {
        setUmsMemberList([]);
        return;
      }
      toggleLoading(true);
      getMemberListV2({
        keyword,
        createSource: [],
        memberLevel: [],
        pageNum: 1,
        pageSize: 10,
      })
        .then((data) => {
          setUmsMemberList(data.list);
        })
        .catch()
        .finally(() => toggleLoading(false));
    }, 300);
  }, [toggleLoading]);

  const handleSearch = useCallback(
    (keyword: string) => {
      debouncedHandleSearch(keyword);
    },
    [debouncedHandleSearch],
  );

  const handleReset = useCallback(() => {
    setUmsMember(undefined);
    setUmsMemberList([]);
    setMemberType(null);
    dispatch({
      type: ActionType.UPDATE_BATCH,
      payload: {
        useIntegration: 0,
        memberId: 0,
      },
    });
  }, [dispatch]);

  const selectModeOptions = useMemo(
    () => [
      {
        label: i18n.t(LOCALS.member_select_mode_scan) || '扫码识别',
        value: MEMBER_SELECT_MODE.SCAN,
      },
      {
        label: i18n.t(LOCALS.member_select_mode_manual) || '手动搜索',
        value: MEMBER_SELECT_MODE.MANUAL,
      },
    ],
    [],
  );

  return (
    <div className="mb-4">
      <h2>{i18n.t(LOCALS.member_details)}</h2>

      <div className="flex items-center mb-4 gap-2">
        <Segmented
          className="shrink-0"
          options={selectModeOptions}
          value={selectMode}
          onChange={(value) => setSelectMode(value as MemberSelectMode)}
        />

        {selectMode === MEMBER_SELECT_MODE.SCAN ? (
          <>
            <Button
              type="primary"
              icon={<ScanOutlined />}
              onClick={toggleScannerOpen}
              loading={loading}
            >
              {i18n.t(LOCALS.scan_member_qr) || '扫码识别会员'}
            </Button>
            {umsMember ? (
              <span className="text-base">
                {umsMember.showName}（{umsMember.email}）
              </span>
            ) : (
              <span className="text-sm text-gray-500">
                {i18n.t(LOCALS.scan_member_qr_hint) ||
                  '请会员在网站个人主页出示二维码'}
              </span>
            )}
          </>
        ) : (
          <Select
            placeholder={i18n.t(LOCALS.phone_or_email) || '邮箱/手机号'}
            className="flex-1"
            filterOption={false}
            notFoundContent={loading ? <Spin size="small" /> : null}
            onSearch={handleSearch}
            showSearch
            value={null}
            onChange={(memberId: UmsMember['id']) => {
              const target = umsMemberList.find((i) => i.id === memberId);
              if (target) {
                selectMember(target as UmsMember);
              }
            }}
            options={umsMemberList.map((i) => ({
              ...i,
              value: i.id,
              label: `${i.showName}(${i.email})`,
            }))}
          />
        )}

        <Button className="ml-auto" onClick={handleReset}>
          {i18n.t(LOCALS.reset)}
        </Button>
        <Button onClick={() => toggleCreateMemberOpen(true)}>
          {i18n.t(LOCALS.add)}
        </Button>
      </div>

      <Modal
        destroyOnClose
        open={scannerOpen}
        onCancel={toggleScannerOpen}
        footer={null}
        title={i18n.t(LOCALS.scan_member_qr) || '扫码识别会员'}
      >
        <p className="mb-4 text-sm text-gray-500">
          {i18n.t(LOCALS.scan_member_qr_hint) ||
            '请会员在网站个人主页出示二维码'}
        </p>
        <BarcodeScanner onScanSuccess={handleMemberScanSuccess} />
      </Modal>

      <UmsMemberTable umsMember={umsMember} memberType={memberType} />

      <CreateMemberDrawer
        open={createMemberOpen}
        onClose={() => toggleCreateMemberOpen(false)}
        setUmsMember={(umsMember) => {
          if (umsMember) {
            selectMember(umsMember);
          }
        }}
      />
    </div>
  );
};

export default UmsMemberSelect;
